import { Enquiry } from '../App/ListPage/ListSection/ShipmentList/ShipmentList';
import { ShipmentProp } from '../App/ListPage/ListSection/ShipmentList/ShipmentListItem/ShipmentListItem';
import type { AtlStatusValue, MultiShipmentPayload, Shipment, ShipmentPayload } from '../models/shipments';
import { fallbackMilestoneParams, getMilestone } from './milestone.utils';

const STARTRACK_PREMIUM_SUBTYPES = [
  'ST Premium',
  'ST Fixed Price Premium',
  'ST Airlock',
  'ST Fixed Price Airlock',
] as const;

export const isStarTrackPremiumArticle = (articleType?: string | null, productSubType?: string | null): boolean => {
  if (articleType !== 'StarTrack') return false;

  return STARTRACK_PREMIUM_SUBTYPES.includes((productSubType ?? '') as (typeof STARTRACK_PREMIUM_SUBTYPES)[number]);
};

/**
 * Transforms a Shipments API payload for use on Track UI and its shared components
 * @param shipmentPayload A payload as expected from Shipments API
 */
export const transformShipmentResponse = (shipmentPayload: ShipmentPayload): Shipment | null => {
  if (!shipmentPayload?.articles?.length) return null;
  const articles = shipmentPayload.articles.map((articlePayload) => {
    const { details, ...articleInfo } = articlePayload;
    const articleDetail = details[0];

    return {
      ...articleInfo,
      ...articleDetail,
      atlStatus: (articleInfo.atlStatus?.statusAttributeValue as AtlStatusValue) || null,
      isStarTrackPremiumArticle: isStarTrackPremiumArticle(articleDetail?.articleType, articleDetail?.productSubType),
    };
  });

  return {
    ...shipmentPayload,
    articles,
  };
};

/**
 * Transforms a payload from Shipments API list and query endpoints for use on Track UI and its shared components
 * @param payload A payload as expected from Shipments API query and watchlist endpoint
 */
export const transformShipmentListResponse = (payload: MultiShipmentPayload): Shipment[] => {
  const shipmentList: Shipment[] = [];
  payload.forEach((shipment) => {
    const transformedShipmentResp = transformShipmentResponse(shipment.shipment);
    transformedShipmentResp && shipmentList.push(transformedShipmentResp);
  });
  return shipmentList;
};

export const MILESTONE_SORT_ORDER = [
  'ATTEMPTED DELIVERY',
  'AWAITING COLLECTION',
  'CONTACT SENDER',
  'RETURNING TO SENDER',
  'RETURNED',
  "IT'S COMING TODAY",
  "IT'S ON ITS WAY",
  'IN TRANSIT',
  'DESPATCHED',
  'POSSIBLE DELAY',
  "WE'VE GOT IT",
  'LABEL CREATED BY SENDER',
  "IT'S BEING PREPARED",
  'DEFAULT', // Any unexpected milestones
  'PROCESSING',
  'STARTED',
  'UNSUCCESSFUL PICKUP',
  'PENDING',
  'DELIVERED',
];

export interface ShipmentCompareParam {
  articles: ShipmentCompareArticle[] | null;
  milestone?: { name: string; dateTime?: string } | null;
}

interface ShipmentCompareArticle {
  events: { milestone?: string | null; dateTime: number }[] | null;
  milestones: { name: string }[] | null;
  trackStatusOfArticle: string;
}

// Helper functions for simplified sorting
const getMilestoneIndex = (milestone: string): number => {
  const index = MILESTONE_SORT_ORDER.indexOf(milestone);
  return index >= 0 ? index : MILESTONE_SORT_ORDER.indexOf('DEFAULT');
};

const getShipmentMilestone = (shipment: ShipmentCompareParam): string => {
  if (shipment?.milestone?.name) {
    return shipment.milestone.name.toUpperCase();
  }
  const article = shipment?.articles?.[0];
  const articleMilestone = article ? getMilestone(article as fallbackMilestoneParams).toUpperCase() : '';
  return articleMilestone;
};

const getShipmentDateTime = (shipment: ShipmentCompareParam): number => {
  return shipment?.articles?.[0]?.events?.[0]?.dateTime || 0;
};

export const compareShipments = (firstShipment: ShipmentCompareParam, nextShipment: ShipmentCompareParam) => {
  // Priority 1: Sort by milestone (shipment-level → article-level fallback)
  const milestoneOrder =
    getMilestoneIndex(getShipmentMilestone(firstShipment)) - getMilestoneIndex(getShipmentMilestone(nextShipment));
  if (milestoneOrder !== 0) return milestoneOrder;

  // Priority 2: Sort by dateTime (article-level event dateTime, newer first)
  return getShipmentDateTime(nextShipment) - getShipmentDateTime(firstShipment);
};

export const matchEnquiriesToShipment = (enquiries: Enquiry[], shipment: Shipment | ShipmentProp) => {
  const matchingCases: string[] = [];

  shipment.articles.forEach((article) => {
    const matchingEnquiry = enquiries?.find((enquiry) => enquiry.articleId === article.articleId);

    if (matchingEnquiry) {
      matchingCases.push(...matchingEnquiry.cases);
    }
  });

  return matchingCases;
};

export const findShipmentByIdentifier = (shipmentArray: Shipment[], trackingIds: string[]) => {
  const identifier = trackingIds.map((trackingId) => trackingId.toUpperCase());
  const shipment = shipmentArray.find((shipment) => {
    const matchConsignment = identifier.some((trackingId) => shipment.consignmentId.toUpperCase() === trackingId);
    if (matchConsignment) return matchConsignment;
    const { articles } = shipment;
    const matchArticle = articles.some(({ articleId, barcode }) => {
      const barcodeStr = barcode?.split('|')?.[0] ?? barcode ?? '';
      return identifier.includes(articleId.toUpperCase()) || identifier.includes(barcodeStr.toUpperCase());
    });
    return matchArticle;
  });
  return shipment ?? null;
};

export const getUniqueDuplicateIds = (
  trackingIds: string[],
  shipmentList: Shipment[]
): { uniqueIds: string[]; duplicates: string[] } => {
  const duplicateSet = new Set<string>(),
    uniqueIds: string[] = [];
  if (!shipmentList.length) return { uniqueIds: trackingIds, duplicates: [] };
  // For each tracking id, check each watchlist shipment, then check each article within the shipment
  trackingIds.forEach((id) => {
    const hasDuplicate = shipmentList.some((shipment) => {
      if (shipment.consignmentId.toUpperCase() === id)
        return duplicateSet.add(shipment.articles?.[0]?.articleId.toUpperCase() ?? id);
      const { articles } = shipment;
      return articles.some((article) => {
        const { articleId, barcode } = article;
        const barcodeStr = barcode?.split('|')?.[0] ?? barcode;
        if (barcodeStr?.toUpperCase() === id)
          return duplicateSet.add(articles.length > 1 ? shipment.consignmentId.toUpperCase() : articleId.toUpperCase());
        if (articleId.toUpperCase() === id)
          return duplicateSet.add(articles.length > 1 ? shipment.consignmentId.toUpperCase() : id);
      });
    });
    if (!hasDuplicate) uniqueIds.push(id);
  });
  return { uniqueIds, duplicates: Array.from(duplicateSet) };
};
